/**
 * Utility functions for handling Nepal timezone (Asia/Kathmandu, UTC+5:45)
 */

export const NEPAL_TIMEZONE = 'Asia/Kathmandu';
const NEPAL_OFFSET_MINUTES = 5 * 60 + 45;

export const isValidNepalDateString = (dateString) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return false;
  }

  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
};

export const normalizeNepalDateString = (dateString, fallbackDateString = getNepalDateString()) => (
  isValidNepalDateString(dateString) ? dateString : fallbackDateString
);

export const getNepalDateString = (date = new Date()) => (
  date.toLocaleDateString('en-CA', { timeZone: NEPAL_TIMEZONE })
);

export const getNepalDayStart = (date = new Date()) => {
  const nepalDateString = getNepalDateString(date);
  return getNepalDayStartFromDateString(nepalDateString);
};

export const getNepalDayStartFromDateString = (dateString) => {
  const normalizedDateString = normalizeNepalDateString(dateString);
  const [year, month, day] = normalizedDateString.split('-').map(Number);
  const nepalMidnight = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));

  return new Date(nepalMidnight.getTime() - (NEPAL_OFFSET_MINUTES * 60 * 1000));
};

export const getNepalNextDayStartFromDateString = (dateString) => {
  const dayStart = getNepalDayStartFromDateString(dateString);
  const nextDay = new Date(dayStart);
  nextDay.setUTCDate(dayStart.getUTCDate() + 1);
  return nextDay;
};

export const getNepalPreviousDayStartFromDateString = (dateString) => {
  const dayStart = getNepalDayStartFromDateString(dateString);
  const previousDay = new Date(dayStart);
  previousDay.setUTCDate(dayStart.getUTCDate() - 1);
  return previousDay;
};

export const getNepalDateDaysAgoFromDateString = (daysAgo, dateString) => {
  const dayStart = getNepalDayStartFromDateString(dateString);
  const previousDate = new Date(dayStart);
  previousDate.setUTCDate(dayStart.getUTCDate() - daysAgo);
  return previousDate;
};

export const shiftNepalDateString = (dateString, days) => {
  const normalizedDateString = normalizeNepalDateString(dateString);
  const [year, month, day] = normalizedDateString.split('-').map(Number);
  const shiftedDate = new Date(Date.UTC(year, month - 1, day + days));

  return shiftedDate.toISOString().slice(0, 10);
};

export const getNepalNextDayStart = (date = new Date()) => {
  const dayStart = getNepalDayStart(date);
  const nextDay = new Date(dayStart);
  nextDay.setUTCDate(dayStart.getUTCDate() + 1);
  return nextDay;
};

export const getNepalPreviousDayStart = (date = new Date()) => {
  const dayStart = getNepalDayStart(date);
  const previousDay = new Date(dayStart);
  previousDay.setUTCDate(dayStart.getUTCDate() - 1);
  return previousDay;
};

export const getNepalDateDaysAgoFromDate = (daysAgo, date = new Date()) => {
  const dayStart = getNepalDayStart(date);
  const previousDate = new Date(dayStart);
  previousDate.setUTCDate(dayStart.getUTCDate() - daysAgo);
  return previousDate;
};

/**
 * Get the current date in Nepal timezone with time set to start of day (00:00:00)
 * @returns {Date} Date object representing start of current day in Nepal timezone (in UTC for Firestore)
 */
export const getNepalTodayStart = () => getNepalDayStart();

/**
 * Get the start of tomorrow in Nepal timezone
 * @returns {Date} Date object representing start of next day in Nepal timezone
 */
export const getNepalTomorrowStart = () => {
  return getNepalNextDayStart();
};

/**
 * Get the start of yesterday in Nepal timezone
 * @returns {Date} Date object representing start of previous day in Nepal timezone
 */
export const getNepalYesterdayStart = () => {
  return getNepalPreviousDayStart();
};

/**
 * Get a date N days ago from today in Nepal timezone
 * @param {number} daysAgo - Number of days to go back
 * @returns {Date} Date object representing start of day N days ago in Nepal timezone
 */
export const getNepalDateDaysAgo = (daysAgo) => {
  return getNepalDateDaysAgoFromDate(daysAgo);
};

/**
 * Format a date to Nepal timezone string
 * @param {Date} date - Date to format
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string in Nepal timezone
 */
export const formatDateInNepalTimezone = (date, options = {}) => {
  return date.toLocaleString('en-US', {
    timeZone: NEPAL_TIMEZONE,
    ...options
  });
};
